/**
 * Poses a fighter's body parts from its state. The hurt volumes are wherever
 * the skeleton puts them, which is what makes ducking, guarding and striking
 * geometric rather than flags.
 */
import { twoBoneIk } from "./math/geometry.ts";
import { cos, sin } from "./math/trig.ts";
import { add, rotateY, type Vec3, v3 } from "./math/vec.ts";
import type { FighterState } from "./state.ts";
import { tuning } from "./tuning.ts";

export const Part = {
  Head: 0,
  Torso: 1,
  UpperArmL: 2,
  UpperArmR: 3,
  ForearmL: 4,
  ForearmR: 5,
  ThighL: 6,
  ThighR: 7,
  ShinL: 8,
  ShinR: 9,
} as const;
export type PartId = (typeof Part)[keyof typeof Part];
export const PART_COUNT = 10;
export const PART_NAMES = [
  "head",
  "torso",
  "upperArmL",
  "upperArmR",
  "forearmL",
  "forearmR",
  "thighL",
  "thighR",
  "shinL",
  "shinR",
] as const;

/** A capsule in world space; a sphere when a equals b. */
export interface Volume {
  part: PartId;
  a: Vec3;
  b: Vec3;
  r: number;
}

/** The fields the skeleton needs; a FighterState or an interpolated render copy. */
export type PoseSource = Pick<
  FighterState,
  | "pos"
  | "facing"
  | "duck"
  | "lean"
  | "stance"
  | "twist"
  | "guard"
  | "handL"
  | "handR"
  | "footL"
  | "footR"
>;

/**
 * How much the side `side` (−1 left, +1 right) leads for a body turned by
 * `turn` blade units (stance, plus twist for the shoulders): +1 fully forward,
 * −1 fully back. Orthodox (+1) leads with the left.
 */
export function leadness(turn: number, side: -1 | 1): number {
  const l = -side * turn;
  return l > 1 ? 1 : l < -1 ? -1 : l;
}

/** Lateral shift (fighter's right is +x) and dip of the upper body for a slip. */
export function leanOffset(lean: number): { sway: number; drop: number } {
  return {
    sway: tuning.movement.leanSway * lean,
    drop: tuning.movement.leanDrop * (lean < 0 ? -lean : lean),
  };
}

/** Joint positions in fighter-local space (x right, y up, z forward), before rotation. */
export interface LocalPose {
  head: Vec3;
  shoulderL: Vec3;
  shoulderR: Vec3;
  elbowL: Vec3;
  elbowR: Vec3;
  handL: Vec3;
  handR: Vec3;
  hipL: Vec3;
  hipR: Vec3;
  kneeL: Vec3;
  kneeR: Vec3;
  footL: Vec3;
  footR: Vec3;
  chestTop: Vec3;
  chestBottom: Vec3;
}

export function localPose(f: PoseSource): LocalPose {
  const b = tuning.body;
  const { sway, drop: leanDrop } = leanOffset(f.lean);
  const drop = tuning.movement.duckDrop * f.duck + leanDrop;
  // Feet and hips stay planted; everything above the waist slips with the lean.
  const shoulderY = b.shoulderY - drop;
  const hipY = b.hipY - tuning.movement.duckDrop * f.duck;
  // Bladed: the lead shoulder and hip sit forward; a strike's twist turns the shoulders through.
  const st = tuning.stance;
  const turn = f.stance + f.twist;
  const hipTurn = f.stance + f.twist * 0.5;
  const shoulderL = v3(-b.shoulderX + sway, shoulderY, turn * st.shoulderZ);
  const shoulderR = v3(b.shoulderX + sway, shoulderY, -turn * st.shoulderZ);
  const hipL = v3(-b.hipX, hipY, hipTurn * st.hipZ);
  const hipR = v3(b.hipX, hipY, -hipTurn * st.hipZ);
  const handL = f.handL;
  const handR = f.handR;
  const footL = f.footL;
  const footR = f.footR;
  return {
    head: v3(sway, b.headY - drop, 0.05 * f.duck),
    shoulderL,
    shoulderR,
    // Guarding: elbows straight down so the forearms stand vertical in front of the face and chest.
    // In stance the elbows stay down and tucked too, a little behind the hands.
    elbowL: twoBoneIk(
      shoulderL,
      handL,
      b.upperArmLength,
      b.forearmLength,
      f.guard ? v3(-0.25, -1, 0.05) : v3(-0.35, -1, -0.15),
    ),
    elbowR: twoBoneIk(
      shoulderR,
      handR,
      b.upperArmLength,
      b.forearmLength,
      f.guard ? v3(0.25, -1, 0.05) : v3(0.35, -1, -0.15),
    ),
    handL,
    handR,
    hipL,
    hipR,
    kneeL: twoBoneIk(hipL, footL, b.thighLength, b.shinLength, v3(0, 0, 1)),
    kneeR: twoBoneIk(hipR, footR, b.thighLength, b.shinLength, v3(0, 0, 1)),
    footL,
    footR,
    // The torso capsule ends below the shoulders so its cap never overlaps the head.
    chestTop: v3(sway, shoulderY - b.torsoRadius * 0.75, 0),
    chestBottom: v3(sway * 0.25, hipY + 0.05, 0),
  };
}

/** World-space hurt volumes, indexed by PartId. */
export function poseFighter(f: PoseSource): Volume[] {
  const p = localPose(f);
  const s = sin(f.facing);
  const c = cos(f.facing);
  const w = (l: Vec3): Vec3 => add(f.pos, rotateY(l, s, c));
  const b = tuning.body;
  const guardArm = f.guard ? b.fistRadius : b.armRadius;
  return [
    { part: Part.Head, a: w(p.head), b: w(p.head), r: b.headRadius },
    { part: Part.Torso, a: w(p.chestTop), b: w(p.chestBottom), r: b.torsoRadius },
    { part: Part.UpperArmL, a: w(p.shoulderL), b: w(p.elbowL), r: b.armRadius },
    { part: Part.UpperArmR, a: w(p.shoulderR), b: w(p.elbowR), r: b.armRadius },
    // Covering, the part is a closed fist and a braced forearm: as thick as the fist.
    { part: Part.ForearmL, a: w(p.elbowL), b: w(p.handL), r: guardArm },
    { part: Part.ForearmR, a: w(p.elbowR), b: w(p.handR), r: guardArm },
    { part: Part.ThighL, a: w(p.hipL), b: w(p.kneeL), r: b.legRadius },
    { part: Part.ThighR, a: w(p.hipR), b: w(p.kneeR), r: b.legRadius },
    { part: Part.ShinL, a: w(p.kneeL), b: w(p.footL), r: b.legRadius },
    { part: Part.ShinR, a: w(p.kneeR), b: w(p.footR), r: b.legRadius },
  ];
}

/**
 * Rest hand in fighter space: the stance guard. The lead hand is out at
 * shoulder height, the rear hand at the chin; a switch blends between them.
 */
export function idleHand(side: -1 | 1, duck: number, lean: number, stance: number): Vec3 {
  const b = tuning.body;
  const st = tuning.stance;
  const { sway, drop } = leanOffset(lean);
  const lead = (leadness(stance, side) + 1) / 2;
  const x = st.rearHand.x + (st.leadHand.x - st.rearHand.x) * lead;
  const y = st.rearHand.y + (st.leadHand.y - st.rearHand.y) * lead;
  const z = st.rearHand.z + (st.leadHand.z - st.rearHand.z) * lead;
  return v3(side * x + sway, b.shoulderY - tuning.movement.duckDrop * duck - drop + y, z);
}

/** Resting foot in fighter space: the lead foot ahead, the rear foot behind. */
export function idleFoot(side: -1 | 1, stance: number): Vec3 {
  const st = tuning.stance;
  return v3(side * st.footX, 0, leadness(stance, side) * st.halfStride);
}

/** Eye position in fighter space (where the look ray starts). Follows the slip. */
export function eyeLocal(duck: number, lean = 0): Vec3 {
  const { sway, drop } = leanOffset(lean);
  return v3(sway, tuning.body.headY - tuning.movement.duckDrop * duck - drop, 0.05 * duck);
}
